import { useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  FileButton,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload, IconFileText, IconPrinter, IconTrash, IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { DocInfo } from "../api/types";
import { useCurrentUser } from "../hooks/useCurrentUser";

export function Documents() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const isAdmin = user.role === "admin";

  const [info, setInfo] = useState<DocInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  async function loadInfo() {
    setLoading(true);
    try {
      setInfo(await api.docInfo());
    } catch {
      notifications.show({ color: "red", message: t("documents.loadFailed") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadInfo();
  }, []);

  async function handleUpload(file: File | null) {
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadDoc(file);
      notifications.show({ color: "green", message: t("documents.docUploaded", { filename: file.name }) });
      await loadInfo();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("documents.uploadFailed"),
        message: detail.slice(0, 200),
      });
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t("documents.confirmDelete"))) return;
    try {
      await api.deleteDoc();
      await loadInfo();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("documents.deleteFailed"),
        message: detail.slice(0, 200),
      });
    }
  }

  return (
    <Container size="md">
      <Title order={2} mb="md">
        {t("documents.title")}
      </Title>

      <Stack gap="md">
        <Card withBorder>
          <Group justify="space-between" mb="sm">
            <Group>
              <IconFileText size={20} />
              <div>
                <Title order={4}>{t("documents.passportTitle")}</Title>
                <Text size="sm" c="dimmed">
                  {t("documents.passportDesc")}
                </Text>
              </div>
            </Group>
            <Group gap="xs">
              <Button
                component="a"
                href={api.passportUrl()}
                target="_blank"
                rel="noopener"
                leftSection={<IconDownload size={16} />}
              >
                {t("documents.downloadPdf")}
              </Button>
              <Button
                component="a"
                href={api.passportA4Url()}
                target="_blank"
                rel="noopener"
                variant="light"
                leftSection={<IconPrinter size={16} />}
              >
                {t("documents.passport2upA4")}
              </Button>
            </Group>
          </Group>
          <Text size="xs" c="dimmed">
            {t("documents.passportHint")}
          </Text>
        </Card>

        <Card withBorder>
          <Group justify="space-between" mb="sm">
            <Group>
              <IconFileText size={20} />
              <div>
                <Title order={4}>{t("documents.docTitle")}</Title>
                <Text size="sm" c="dimmed">
                  {t("documents.docDesc")}
                </Text>
              </div>
            </Group>
            {isAdmin && (
              <Group gap="xs">
                <FileButton onChange={handleUpload} accept="application/pdf,.pdf">
                  {(props) => (
                    <Button
                      {...props}
                      variant="light"
                      leftSection={<IconUpload size={16} />}
                      loading={uploading}
                    >
                      {info?.present ? t("documents.replace") : t("documents.uploadPdf")}
                    </Button>
                  )}
                </FileButton>
                {info?.present && (
                  <Button
                    color="red"
                    variant="subtle"
                    leftSection={<IconTrash size={16} />}
                    onClick={handleDelete}
                  >
                    {t("common.delete")}
                  </Button>
                )}
              </Group>
            )}
          </Group>

          {loading ? (
            <Loader size="sm" />
          ) : info?.present ? (
            <Stack gap={4}>
              <Group gap="xs">
                <Badge color="green" variant="light">
                  {t("documents.available")}
                </Badge>
                {info.size && (
                  <Text size="xs" c="dimmed">
                    {(info.size / 1024).toFixed(1)} KB
                  </Text>
                )}
                {info.original_filename && (
                  <Text size="xs" c="dimmed">
                    · {info.original_filename}
                  </Text>
                )}
                {info.uploaded_by && (
                  <Text size="xs" c="dimmed">
                    · {t("documents.uploadedBy", { user: info.uploaded_by })}
                  </Text>
                )}
              </Group>
              <Anchor href={api.docUrl()} target="_blank" rel="noopener" size="sm">
                {t("documents.downloadDoc")}
              </Anchor>
            </Stack>
          ) : isAdmin ? (
            <Alert color="yellow" variant="light">
              {t("documents.noneAdmin")}
            </Alert>
          ) : (
            <Alert color="gray" variant="light">
              {t("documents.noneUser")}
            </Alert>
          )}
        </Card>
      </Stack>
    </Container>
  );
}
